import { computed, onBeforeUnmount, reactive, toRefs, watch } from 'vue';
import { PAYMENT_METHODS, widgetViews } from '@/utils';
import { verifyCheckoutCaptcha } from '@/services/captcha';

export default function useZelleCheckout({ store, form, companyRequired }) {
  const state = reactive({ saving: false, reporting: false, error: '' });
  const active = computed(() => store.state.donation.paymentMethod === PAYMENT_METHODS.ZELLE);
  const draftFingerprint = computed(() => store.getters['donation/zelleDraftFingerprint']);
  const instructionsReady = computed(() => !!store.state.donation.zelleInstructions && store.state.donation.zelleDraftFingerprint === draftFingerprint.value);
  const fingerprint = computed(() => store.getters['donation/zelleCheckoutFingerprint']);
  const detailsValid = computed(
    () =>
      active.value &&
      store.getters['validation/isDonationValid'] &&
      store.state.validation.isCustomQuestionsValid &&
      (!companyRequired.value || !!store.state.donation.donorDetails.companyName?.trim())
  );
  const ready = computed(
    () => detailsValid.value && !!store.state.donation.zelleInstructions && store.state.donation.zelleRequestFingerprint === fingerprint.value
  );
  let pendingSave;
  let timer;
  let disposed = false;

  function scheduleSave() {
    clearTimeout(timer);
    if (active.value && !disposed) timer = setTimeout(() => !state.reporting && save(), store.state.donation.zelleInstructions ? 600 : 0);
  }

  function save() {
    if (disposed || !active.value || state.saving || ready.value) return;
    pendingSave = saveDetails();
    return pendingSave;
  }

  async function saveDetails() {
    state.saving = true;
    state.error = '';
    const submittedFingerprint = fingerprint.value;
    const submittedDraftFingerprint = draftFingerprint.value;
    const submittedDetailsValid = detailsValid.value;
    try {
      const draft = !detailsValid.value || !form.value || (await form.value.hasValidationErrors());
      if (draft && instructionsReady.value) return;
      await verifyCheckoutCaptcha(store);
      if (disposed || !active.value || submittedFingerprint !== fingerprint.value) return;
      if (!draft && !detailsValid.value) return;
      await store.dispatch('donation/createZelleDonation', { draft });
    } catch (error) {
      state.error = error.message;
    } finally {
      state.saving = false;
      if (submittedFingerprint !== fingerprint.value || submittedDraftFingerprint !== draftFingerprint.value || submittedDetailsValid !== detailsValid.value)
        scheduleSave();
    }
  }

  async function reportSent() {
    if (disposed || !detailsValid.value || state.reporting || !form.value) return;
    state.reporting = true;
    state.error = '';
    try {
      if (await form.value.hasValidationErrors()) return;
      await pendingSave;
      if (disposed || !detailsValid.value) return;
      do {
        const submittedFingerprint = fingerprint.value;
        await save();
        if (fingerprint.value === submittedFingerprint) break;
      } while (!disposed && detailsValid.value && !state.error);
      if (disposed || !ready.value) return;
      await store.dispatch('donation/reportZelleSent');
      if (!disposed) store.commit('SET_WIDGET_VIEW', widgetViews.CONFIRMATION_DONATION);
    } catch (error) {
      state.error = error.message;
    } finally {
      state.reporting = false;
    }
  }

  watch([active, detailsValid, fingerprint, draftFingerprint], scheduleSave, { immediate: true });
  onBeforeUnmount(() => {
    disposed = true;
    clearTimeout(timer);
  });

  return reactive({ ...toRefs(state), active, detailsValid, instructionsReady, ready, save, reportSent });
}
